import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GitCompare, X, Check, Minus } from "lucide-react";
import { api } from "../lib/api";
import { useCompare } from "../context/CompareContext";
import { useLang } from "../context/LanguageContext";
import { formatJOD } from "../components/ProductCard";
import { resolveImg } from "../lib/api";

export default function Compare() {
  const { ids, toggle, clear } = useCompare();
  const [products, setProducts] = useState([]);
  const { t, tBrand, tType } = useLang();

  const ROWS = [
    { key: "brand", label: t("compare.row.brand"), transform: (v) => tBrand(v) },
    { key: "category", label: t("compare.row.category"), transform: (v) => tType(v) },
    { key: "price", label: t("compare.row.price"), format: (v) => formatJOD(v) },
    { key: "sale_price", label: t("compare.row.salePrice"), format: (v) => (v ? formatJOD(v) : "—") },
    { key: "stock", label: t("compare.row.stock") },
    { spec: "btu", label: t("compare.row.btu") },
    { spec: "type", label: t("compare.row.type"), transform: (v) => tType(v) },
    { spec: "energy_rating", label: t("compare.row.energy") },
    { spec: "color", label: t("compare.row.color") },
    { spec: "warranty", label: t("compare.row.warranty") },
  ];

  useEffect(() => {
    if (ids.length === 0) {
      setProducts([]);
      return;
    }
    Promise.all(
      ids.map(() => api.get(`/products?limit=200`).then((r) => r.data))
    )
      .then(([all]) => {
        setProducts(all.filter((p) => ids.includes(p.id)));
      })
      .catch(() => setProducts([]));
  }, [ids]);

  if (ids.length === 0) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        <GitCompare size={48} className="mx-auto text-electric" />
        <h1 className="mt-6 font-display font-black text-3xl">{t("compare.empty")}</h1>
        <p className="mt-3 text-slate-600">{t("compare.emptyHint")}</p>
        <Link to="/shop" className="mt-6 inline-flex h-11 px-6 items-center bg-ink text-white rounded-full text-xs font-mono uppercase tracking-[0.2em] hover:bg-electric">
          {t("common.browseUnits")}
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="compare-page" className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500">{t("compare.eyebrow")}</div>
          <h1 className="mt-2 font-display font-extrabold text-4xl tracking-tight">{t("compare.title")}</h1>
        </div>
        <button
          onClick={clear}
          className="text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-electric"
        >
          {t("compare.clearAll")}
        </button>
      </div>

      <div className="mt-10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="w-40 text-left text-[10px] font-mono uppercase tracking-widest text-slate-500 align-top">&nbsp;</th>
              {products.map((p) => (
                <th key={p.id} className="p-4 align-top text-left">
                  <div className="bg-white rounded-2xl p-4 shadow-[0_4px_20px_-8px_rgba(0,0,0,0.08)] relative min-w-[200px]">
                    <button
                      onClick={() => toggle(p.id)}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-secondary hover:bg-destructive hover:text-white grid place-items-center"
                      aria-label={t("common.remove")}
                    >
                      <X size={12} />
                    </button>
                    <Link to={`/product/${p.slug}`}>
                      {p.images?.[0] && (
                        <img src={resolveImg(p.images[0])} alt={p.name} className="w-full h-32 object-contain p-2" />
                      )}
                      <div className="mt-2 font-display font-bold text-sm leading-tight">{p.name}</div>
                    </Link>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row) => (
              <tr key={row.label} className="border-t border-black/5">
                <td className="py-3 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                  {row.label}
                </td>
                {products.map((p) => {
                  let v = row.spec ? (p.specs || {})[row.spec] : p[row.key];
                  if (row.transform) v = row.transform(v);
                  if (row.format) v = row.format(v);
                  return (
                    <td key={p.id} className="py-3 px-4 font-mono text-sm">
                      {v ? <span className="inline-flex items-center gap-1.5"><Check size={12} className="text-electric" /> {v}</span> : <Minus size={14} className="text-slate-300" />}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
